import { Link, useParams } from 'react-router-dom'
import { Layout } from '../components/layout/layout'
import { useAuth } from '../hooks/use-auth'
import { useDocument, useDocumentBySlug } from '../hooks/use-documents'
import { ApiError } from '../lib/api-client'
import { canEditDocument, documentReadPath } from '../lib/document-route'
import { DocumentReaderPage } from './document-reader-page'

function looksLikeDocumentId(value: string | undefined): value is string {
  return Boolean(value && /^[0-9A-Za-z]{21}$/.test(value))
}

function LoadingState() {
  return (
    <div className="aw-page" aria-busy="true">
      <div className="aw-page-body flex items-center justify-center">
        <div className="space-y-3 w-full max-w-2xl px-8">
          {[0, 1, 2, 3, 4].map((item) => (
            <div
              key={item}
              className="h-4 animate-pulse rounded"
              style={{ width: `${58 + item * 8}%`, background: 'var(--paper-3)' }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface RouteStateProps {
  title: string
  body: string
  action?: { label: string; onClick: () => void }
}

function RouteState({ title, body, action }: RouteStateProps) {
  return (
    <div className="aw-page">
      <div className="aw-page-body flex items-center justify-center">
        <div className="grid max-w-sm gap-3 text-center">
          <h1 className="text-xl font-semibold" style={{ color: 'var(--ink)' }}>
            {title}
          </h1>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {body}
          </p>
          <Link to="/doc" className="text-sm font-medium text-brand-400 hover:underline">
            Open knowledge library
          </Link>
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className="text-sm font-medium text-brand-400 hover:underline"
            >
              {action.label}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function EditUnavailableState({
  sourceBacked,
  readPath,
}: {
  sourceBacked: boolean
  readPath: string
}) {
  return (
    <div className="aw-page">
      <div className="aw-page-body flex items-center justify-center">
        <div className="grid max-w-sm gap-3 text-center">
          <h1 className="text-xl font-semibold" style={{ color: 'var(--ink)' }}>
            {sourceBacked ? 'Source-backed document' : 'Read-only access'}
          </h1>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {sourceBacked
              ? 'This document is compiled from a synced source and cannot be edited here.'
              : 'Your workspace role does not allow document editing.'}
          </p>
          <Link to={readPath} className="text-sm font-medium text-brand-400 hover:underline">
            Return to document
          </Link>
        </div>
      </div>
    </div>
  )
}

interface DocumentAccessRouteProps {
  mode: 'reader' | 'editor'
  lookup?: 'auto' | 'id'
}

export function DocumentAccessRoute({ mode, lookup = 'auto' }: DocumentAccessRouteProps) {
  const { slug, id } = useParams<{ slug?: string; id?: string }>()
  const routeKey = lookup === 'id' ? id : slug
  const { role } = useAuth()
  const bySlug = useDocumentBySlug(lookup === 'auto' ? routeKey : undefined)
  const slugNotFound = bySlug.error instanceof ApiError && bySlug.error.status === 404
  const legacyIdFallbackActive =
    lookup === 'auto' && looksLikeDocumentId(routeKey) && bySlug.isError && slugNotFound
  const idLookupActive = lookup === 'id' || legacyIdFallbackActive
  const byId = useDocument(idLookupActive ? (routeKey ?? null) : null)
  const queries = lookup === 'id' ? [byId] : legacyIdFallbackActive ? [bySlug, byId] : [bySlug]
  const canonicalSlugDocument = bySlug.data?.slug === routeKey ? bySlug.data : undefined
  const doc =
    lookup === 'id'
      ? byId.data
      : (canonicalSlugDocument ?? (legacyIdFallbackActive ? byId.data : undefined))

  if (doc) {
    const editable = canEditDocument(doc, role)
    if (mode === 'reader') {
      return (
        <DocumentReaderPage
          doc={doc}
          canEdit={editable}
          canResolveConflict={role === 'admin'}
          role={role}
        />
      )
    }
    if (!editable) {
      return (
        <EditUnavailableState
          sourceBacked={Boolean(doc.sourceProvenance)}
          readPath={documentReadPath(doc)}
        />
      )
    }
    return <Layout document={doc} canCreate={role === 'admin' || role === 'editor'} />
  }

  if (queries.some((query) => query.isLoading)) return <LoadingState />

  const errors = queries
    .map((query) => query.error)
    .filter((error): error is ApiError => error instanceof ApiError)
  if (
    queries.every((query) => query.isError) &&
    errors.length === queries.length &&
    errors.every((error) => error.status === 404)
  ) {
    return (
      <RouteState
        title="Document not found"
        body="This document may have been moved, deleted, or never synced."
      />
    )
  }

  if (queries.every((query) => query.isError)) {
    const error = errors.find((item) => item.status !== 404) ?? errors[0]
    if (error?.status === 401) {
      return <RouteState title="Sign in required" body="Sign in again to read this document." />
    }
    if (error?.status === 403) {
      return (
        <RouteState
          title="Access denied"
          body="You do not have permission to read this document."
        />
      )
    }
    return (
      <RouteState
        title="Could not load document"
        body="AgentWiki could not retrieve this document. Your content has not been changed."
        action={{
          label: 'Try again',
          onClick: () => {
            if (lookup === 'auto') void bySlug.refetch()
            if (idLookupActive) void byId.refetch()
          },
        }}
      />
    )
  }

  return <LoadingState />
}
